import { Address } from "@/dto/address";
import { Locale } from "@/dto/locale";
import { localizedPhoneNumber } from "@/components/_localized/localizationUtils";
import Link from "next/link";
import { usePathname } from "next/navigation";
import clsx from "clsx";
import { ROUTE_CONFIRMATION } from "@/utils/constants";
import { useNativeActions } from "@/utils/useNativeActions";
import type { Merchant } from "@/dto/merchant";
import {
  getHardNavigationHref,
  shouldHardNavigate,
} from "@/utils/hardNavigation";

interface MerchantNameAndAddressProps {
  merchantName?: string;
  isSimpleStorefront: boolean;
  isStandaloneGiftCard: boolean;
  merchantAddress: Address;
  merchantPhone: string;
  merchantLocale?: Locale;
  textRef?: React.RefObject<HTMLParagraphElement | null>;
  isMultiline: boolean;
  showLink?: boolean;
  linkHref: string | object;
  logoNode?: React.ReactNode;
  merchant?: Pick<
    Merchant,
    | "isRetail"
    | "isService"
    | "isRestaurant"
    | "isHybrid"
    | "isEssentialMerchant"
    | "hasOnlyRetailItems"
  >;
}

export const MerchantNameAndAddress = ({
  merchantName = "Default Merchant Name",
  isSimpleStorefront,
  isStandaloneGiftCard,
  merchantAddress,
  merchantPhone,
  merchantLocale = "en-US",
  textRef,
  isMultiline,
  showLink,
  linkHref,
  logoNode,
  merchant,
}: MerchantNameAndAddressProps) => {
  const pathname = usePathname();
  const isConfirmationPage = pathname?.includes(ROUTE_CONFIRMATION) ?? false;
  const { isCmaApp } = useNativeActions();

  const containerClasses = clsx("flex flex-col sm:max-h-none relative", {
    "text-center sm:text-left": isSimpleStorefront,
  });

  const nameSpan = (
    <span
      className={clsx("leading-normal break-all sm:max-w-none sm:text-[32px]", {
        "text-[16px]block truncate text-left font-medium shrink min-w-0":
          isCmaApp,
        "break-all font-normal": !isCmaApp,
        "text-[18px] font-normal": !isCmaApp && isMultiline,
        "text-[22px] font-normal": !isCmaApp && !isMultiline,
      })}
    >
      {merchantName}
    </span>
  );

  const nameSection = isCmaApp ? (
    <div className="flex items-center justify-center gap-2 w-full max-w-full">
      {logoNode}
      {nameSpan}
    </div>
  ) : (
    nameSpan
  );

  const merchantInfo = (
    <div className={containerClasses}>
      <span
        ref={textRef}
        aria-hidden="true"
        className={clsx(
          "absolute top-0 left-0 w-full opacity-0 pointer-events-none -z-10",
          "leading-normal font-normal break-all sm:max-w-none sm:text-[32px]",
          "text-[22px]"
        )}
      >
        {merchantName}
      </span>
      {nameSection}

      {!isConfirmationPage && (isSimpleStorefront || isStandaloneGiftCard) && (
        <div className={clsx("text-[15px]", { "text-center": isCmaApp })}>
          <p>{Object.values(merchantAddress).filter(Boolean).join(", ")}</p>
          <p>{localizedPhoneNumber(merchantPhone, merchantLocale)}</p>
        </div>
      )}
    </div>
  );

  const shouldUseHardNavigation =
    merchant && shouldHardNavigate(linkHref, merchant);

  if (!showLink) return merchantInfo;

  if (shouldUseHardNavigation) {
    return (
      <a
        href={getHardNavigationHref(linkHref)}
        className="min-w-0 flex-shrink"
      >
        {merchantInfo}
      </a>
    );
  }

  return (
    <Link href={linkHref} className="min-w-0 flex-shrink">
      {merchantInfo}
    </Link>
  );
};
